
import { useState, useEffect } from "react";
import { Video } from "@/types/database";
import { format } from "date-fns";
import { TimeRange } from "@/components/TimeRangeSelector";
import { useLazyQuery } from "@apollo/client";
import { GET_USERS, GET_VIDEOS, GET_ADMIN_DASHBOARD_DATA } from "@/integrations/apollo/operations";

export interface DashboardData {
  totalLoggedTime: number;
  totalPaidUsers: number;
  totalVideos: number;
  newSignups: number;
  avgVideosPerUser: number;
  apiStatus: 'operational' | 'degraded' | 'down';
}

export interface SourceData {
  name: string;
  value: number;
  color: string;
}

// Raw API source data type
export type ApiSourceData = [string, number];

// Raw API timeline data type
export type ApiTimelineData = [string, number];

export interface VideoTimelineData {
  name: string;
  videos: number;
}

export interface User {
  id: string;
  email: string;
  name?: string;
  source?: string;
  signupDate: string;
  totalVideos: number;
  totalLoggedTime: number;
  lastActive: string;
  plan: "basic" | "pro" | "enterprise";
  role: string;
  created_at: string;
  updated_at: string;
}

// Helper function to get color for a source
const getSourceColor = (source: string): string => {
  const sourceColors: {[key: string]: string} = {
    zillow: '#006aff',
    airbnb: '#ff395c',
    propertysimple: '#f05a27'
  };
  return sourceColors[source.toLowerCase()] || '#94a3b8'; // Default color
};

// Helper function to get nice display name for a source
const getSourceDisplayName = (source: string): string => {
  const sourceNames: {[key: string]: string} = {
    zillow: 'Zillow',
    airbnb: 'Airbnb',
    propertysimple: 'Property Simple',
    vrbo: 'VRBO',
    booking: 'Booking.com',
    tripadvisor: 'TripAdvisor',
    redfin: 'Redfin',
    realtor: 'Realtor.com'
  };
  return sourceNames[source.toLowerCase()] || source; // Return original if no mapping
};

export const useAdminDashboardData = (timeRange: TimeRange) => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [overviewData, setOverviewData] = useState<DashboardData>({
    totalLoggedTime: 0,
    totalPaidUsers: 0,
    totalVideos: 0,
    newSignups: 0,
    avgVideosPerUser: 0,
    apiStatus: 'operational'
  });
  const [sourceBreakdownData, setSourceBreakdownData] = useState<SourceData[]>([]);
  const [videoTimelineData, setVideoTimelineData] = useState<VideoTimelineData[]>([]);

  // queries
  const [getUsers] = useLazyQuery(GET_USERS);
  const [getVideos] = useLazyQuery(GET_VIDEOS);
  const [getAdminDashboardData] = useLazyQuery(GET_ADMIN_DASHBOARD_DATA);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      // Fetch users
      const { data: usersData, error: usersError } = await getUsers();

      if (usersError) throw usersError;

      // Fetch videos
      const { data: videosData, error: videosError } = await getVideos();

      if (videosError) throw videosError;

      // Fetch admin dashboard data
      const { data: adminData, error: adminError } = await getAdminDashboardData({
        variables: {
          start_date: parseInt(timeRange.startDate / 1000),
          end_date: parseInt(timeRange.endDate / 1000),
        }
      });

      if (adminError) throw adminError;

      // Set data
      if (videosData) setVideos(videosData.videos);
      if (usersData) setUsers(usersData.users);
      if (adminData?.usage_stats) {
        setOverviewData({
          totalLoggedTime: 0, // Not provided in new API
          totalPaidUsers: adminData.usage_stats.paid_users_count ?? 0,
          totalVideos: adminData.usage_stats.video_count ?? 0,
          newSignups: adminData.usage_stats.new_users_count ?? 0,
          avgVideosPerUser: adminData.usage_stats.avg_videos_per_user ?? 0,
          apiStatus: 'operational', // Default or could be set from another field
        });

        // Use the raw API data directly without transformation
        if (adminData.usage_stats.videos_count_by_website_source_graph_data) {
          // Convert API source data to our interface format with nice display names
          const sourceData = adminData.usage_stats.videos_count_by_website_source_graph_data.map(([source, value]: ApiSourceData) => {
            return {
              name: getSourceDisplayName(source), // Use display name instead of raw source
              value,
              color: getSourceColor(source)
            };
          });
          setSourceBreakdownData(sourceData);
        }

        // Use the raw timeline data directly
        if (adminData.usage_stats.videos_created_by_day_graph_data) {
          const timelineData = adminData.usage_stats.videos_created_by_day_graph_data.map(([date, count]: ApiTimelineData) => {
            return {
              name: date,
              videos: count
            };
          });
          setVideoTimelineData(timelineData);
        }
      } else {
        // Fallback to legacy processing if no usage_stats
        const sourceData = processSourceBreakdown(videos, timeRange);
        setSourceBreakdownData(sourceData);
        const timelineData = generateVideoTimelineData(timeRange, videos);
        setVideoTimelineData(timelineData);
      }
    } catch (error) {
      console.error("Error fetching admin dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Process overview data
  const processOverviewData = (users: User[], videos: Video[], timeRange: TimeRange): DashboardData => {
    const { startDate, endDate } = timeRange;

    // Filter videos and users based on time range
    const videosInRange = videos.filter(video => {
      const videoDate = new Date(video.created_at);
      return videoDate >= startDate && videoDate <= endDate;
    });

    const newUsersInRange = users.filter(user => {
      const signupDate = new Date(user.signupDate);
      return signupDate >= startDate && signupDate <= endDate;
    });

    // Filter users active within the date range
    const activeUsersInRange = users.filter(user => {
      const lastActiveDate = new Date(user.lastActive);
      return lastActiveDate >= startDate && lastActiveDate <= endDate;
    });

    // Calculate total logged time only within the range
    const totalLoggedTime = activeUsersInRange.reduce((sum, user) => sum + user.totalLoggedTime, 0);

    // Calculate average videos per user based on range-filtered data
    const avgVideosPerUser = activeUsersInRange.length
      ? Number((videosInRange.length / activeUsersInRange.length).toFixed(1))
      : 0;

    return {
      totalLoggedTime,
      totalPaidUsers: activeUsersInRange.length,
      totalVideos: videosInRange.length,
      newSignups: newUsersInRange.length,
      avgVideosPerUser,
      apiStatus: 'operational'
    };
  };

  // Process source breakdown
  const processSourceBreakdown = (videos: Video[], timeRange: TimeRange): SourceData[] => {
    const { startDate, endDate } = timeRange;

    // Filter videos to those within the time range
    const videosInRange = videos.filter(video => {
      const videoDate = new Date(video.created_at);
      return videoDate >= startDate && videoDate <= endDate;
    });

    const sourceCounts: {[key: string]: number} = {};
    videosInRange.forEach(video => {
      const source = video.source || 'other';
      sourceCounts[source] = (sourceCounts[source] || 0) + 1;
    });

    // Define colors for different sources
    const sourceColors: {[key: string]: {name: string, color: string}} = {
      zillow: {
        name: 'Zillow',
        color: '#006aff'
      },
      airbnb: {
        name: 'Airbnb',
        color: '#ff395c'
      },
      propertysimple: {
        name: 'Property Simple',
        color: '#f05a27'
      },
      other: {
        name: 'Other',
        color: '#94a3b8'
      }
    };
    const supportedSources = Object.keys(sourceColors);

    delete supportedSources['other'];

    return Object.entries(sourceCounts).map(([name, value]) => {
      const key = supportedSources.find(s => s === name.toLowerCase()) || 'other';

      // Use the nicer name from sourceColors
      return {...sourceColors[key], value};
    }).sort((a, b) => b.value - a.value).slice(0, 5); // Top 5 sources
  };

  // Generate video timeline data without grouping
  const generateVideoTimelineData = (timeRange: TimeRange, videos: Video[]): VideoTimelineData[] => {
    const { startDate, endDate } = timeRange;

    // Filter videos within the time range
    const filteredVideos = videos.filter(video => {
      const videoDate = new Date(video.created_at);
      return videoDate >= startDate && videoDate <= endDate;
    });

    // Create a map to count videos by their date
    const videosByDate: Record<string, number> = {};

    // Count videos by date
    filteredVideos.forEach(video => {
      const videoDate = new Date(video.created_at);
      let dateKey;

      // Format the date key based on the time range
      if (timeRange.label.includes("Today") || timeRange.label.includes("Yesterday")) {
        dateKey = format(videoDate, 'ha'); // Hour format
      } else {
        dateKey = format(videoDate, 'MMM d'); // Day format
      }

      // Increment the count for this date
      videosByDate[dateKey] = (videosByDate[dateKey] || 0) + 1;
    });

    // Convert the map to VideoTimelineData array
    return Object.entries(videosByDate).map(([name, count]) => ({
      name,
      videos: count
    }));
  };

  // Fetch data when component mounts or timeRange changes
  useEffect(() => {
    fetchData();
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoading,
    users: users.filter(user => {
      const lastActiveDate = new Date(user.lastActive);
      return lastActiveDate >= timeRange.startDate && lastActiveDate <= timeRange.endDate;
    }),
    allUsers: users,
    videos: videos.filter(video => {
      const videoDate = new Date(video.created_at);
      return videoDate >= timeRange.startDate && videoDate <= timeRange.endDate;
    }),
    overviewData,
    sourceBreakdownData,
    videoTimelineData,
    refreshData: fetchData
  };
};
